import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '@/environments/environment';
import {Page} from '@/app/core/models/page';
import {BattlePepsCurrent} from '@/app/core/models/battlepeps/battlePepsCurrent';
import {TierReward} from '@/app/core/models/battlepeps/tierReward';
import {BattlePepsTierProgress} from '@/app/core/models/battlepeps/battlePepsTierProgress';
import {RankTier} from '@/app/core/models/battlepeps/rankTier';
import {BattlePepsLeaderboardEntry} from '@/app/core/models/battlepeps/battlePepsLeaderboardEntry';
import {BattlePepsQuestForPlayer} from '@/app/core/models/battlepeps/battlePepsQuestForPlayer';
import {CompletedQuest} from '@/app/core/models/battlepeps/completedQuest';
import {BattlePepsProgress} from '@/app/core/models/battlepeps/battlePepsProgress';
import {VerifyQuestResult} from '@/app/core/models/battlepeps/verifyQuestResult';

@Injectable({
	providedIn: 'root'
})
export class BattlePepsService {

	private readonly SERVICE_URI = `${environment.backendUrl}/battlepeps`;

	constructor(
		private readonly http: HttpClient
	) {}

	getCurrent(): Observable<BattlePepsCurrent> {
		return this.http.get<BattlePepsCurrent>(`${this.SERVICE_URI}/current`);
	}

	getCurrentTiers(): Observable<BattlePepsTierProgress[]> {
		return this.http.get<BattlePepsTierProgress[]>(`${this.SERVICE_URI}/current/tiers`);
	}

	getCurrentRewards(): Observable<TierReward[]> {
		return this.http.get<TierReward[]>(`${this.SERVICE_URI}/current/rewards`);
	}

	claimTier(tierNumber: number): Observable<TierReward> {
		return this.http.post<TierReward>(`${this.SERVICE_URI}/current/rewards/${tierNumber}/claim`, {});
	}

	claimAll(): Observable<TierReward[]> {
		return this.http.post<TierReward[]>(`${this.SERVICE_URI}/current/rewards/claim-all`, {});
	}

	getRanks(): Observable<RankTier[]> {
		return this.http.get<RankTier[]>(`${this.SERVICE_URI}/ranks`);
	}

	getLeaderboard(battlePepsId: string, page: number = 0): Observable<Page<BattlePepsLeaderboardEntry>> {
		const params = new HttpParams().set('page', page);
		return this.http.get<Page<BattlePepsLeaderboardEntry>>(`${this.SERVICE_URI}/${battlePepsId}/leaderboard`, {params});
	}

	getCurrentQuests(): Observable<BattlePepsQuestForPlayer[]> {
		return this.http.get<BattlePepsQuestForPlayer[]>(`${this.SERVICE_URI}/current/quests`);
	}

	getCompletedQuests(): Observable<CompletedQuest[]> {
		return this.http.get<CompletedQuest[]>(`${this.SERVICE_URI}/current/quests/completed`);
	}

	declareQuest(questId: string): Observable<{ message: string }> {
		return this.http.post<{ message: string }>(`${this.SERVICE_URI}/current/quests/${questId}/declare`, {});
	}

	declareQuestWithImage(questId: string, image: File): Observable<{ message: string }> {
		const formData = new FormData();
		formData.append('image', image);
		return this.http.post<{ message: string }>(`${this.SERVICE_URI}/current/quests/${questId}/declare-with-image`, formData);
	}

	verifyQuest(questId: string): Observable<VerifyQuestResult> {
		return this.http.post<VerifyQuestResult>(`${this.SERVICE_URI}/current/quests/${questId}/verify`, {});
	}

	/** Quête SUB_YOUTUBE : URL d'autorisation Google pour le flux one-shot (redirection pleine page). */
	getYoutubeAuthorizeUrl(questId: string): Observable<{ url: string }> {
		return this.http.get<{ url: string }>(`${this.SERVICE_URI}/current/quests/${questId}/youtube-authorize`);
	}

	// TEMPORAIRE — outil de test back+front pour passer un palier sans dépendre d'une vraie quête. À supprimer après tests (cf. backend).
	skipCurrentTier(): Observable<BattlePepsProgress> {
		return this.http.post<BattlePepsProgress>(`${this.SERVICE_URI}/current/tiers/skip`, {});
	}
}
